import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import './FitnessAdminDashboard.css';

const API = process.env.REACT_APP_API_URL;

const STATUS_COLORS = {
  DRAFT: 'draft',
  PENDING: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected'
};

// ── Metric card (overview) ───────────────────────────────────
const MetricCard = ({ label, data }) => (
  <div className="fad-metric-card">
    <h3>{label}</h3>
    <div className="fad-metric-row">
      <span className="fad-metric-total">{data.total}</span>
      <span className="fad-badge fad-badge-pending">{data.PENDING} pending</span>
    </div>
    <div className="fad-metric-breakdown">
      <span className="fad-badge fad-badge-approved">{data.APPROVED} approved</span>
      <span className="fad-badge fad-badge-rejected">{data.REJECTED} rejected</span>
      <span className="fad-badge fad-badge-draft">{data.DRAFT} draft</span>
    </div>
  </div>
);

// ── Reject modal ─────────────────────────────────────────────
const RejectModal = ({ item, onConfirm, onCancel }) => {
  const [reason, setReason] = useState('');
  return (
    <div className="fad-modal-overlay">
      <div className="fad-modal">
        <h3>Reject "{item.title}"</h3>
        <p>Optionally provide a reason to the coach:</p>
        <textarea
          value={reason}
          onChange={e => setReason(e.target.value)}
          placeholder="e.g. Inappropriate content, incomplete instructions…"
          rows={4}
        />
        <div className="fad-modal-actions">
          <button className="fad-btn fad-btn-danger" onClick={() => onConfirm(reason)}>
            Confirm Reject
          </button>
          <button className="fad-btn fad-btn-secondary" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Content table (shared by all 3 tabs) ─────────────────────
const ContentTable = ({ type, items, onModerate, onDelete }) => {
  const [rejectTarget, setRejectTarget] = useState(null);

  const detailLine = (item) => {
    if (type === 'exercises') {
      return [item.category, item.difficulty, item.primaryMuscles?.join(', ')].filter(Boolean).join(' · ');
    }
    if (type === 'workouts') {
      const exCount = item.exercises?.length || 0;
      return [item.category, item.difficulty, `${exCount} exercise${exCount !== 1 ? 's' : ''}`].filter(Boolean).join(' · ');
    }
    // programs
    const woCount = item.workouts?.length || 0;
    return [item.category, item.difficulty, `${woCount} week${woCount !== 1 ? 's' : ''}`, item.pricingType === 'paid' ? `$${(item.price / 100).toFixed(2)}` : 'Free'].filter(Boolean).join(' · ');
  };

  if (items.length === 0) {
    return <p className="fad-empty">No {type} match the current filters.</p>;
  }

  return (
    <>
      {rejectTarget && (
        <RejectModal
          item={rejectTarget}
          onConfirm={(reason) => {
            onModerate(type, rejectTarget._id, 'REJECTED', reason);
            setRejectTarget(null);
          }}
          onCancel={() => setRejectTarget(null)}
        />
      )}
      <div className="fad-content-list">
        {items.map(item => {
          const status = item.moderation_status || 'DRAFT';
          const isPending = status === 'PENDING';
          const creator = item.creatorName || item.affiliateBusinessName || (item.createdBy ? 'coach' : 'System');
          const creatorEmail = item.creatorEmail || item.affiliateEmail || '';

          return (
            <div key={item._id} className="fad-content-card">
              <div className="fad-card-header">
                <div className="fad-card-title-row">
                  <h4 className="fad-card-title">{item.title}</h4>
                  <span className={`fad-status-badge fad-status-${STATUS_COLORS[status]}`}>
                    {status}
                  </span>
                </div>
                <p className="fad-card-detail">{detailLine(item)}</p>
              </div>

              {item.description && (
                <p className="fad-card-description">{item.description}</p>
              )}

              <div className="fad-card-meta">
                <span>
                  <strong>Creator:</strong> {creator}
                  {creatorEmail && <span className="fad-meta-email"> ({creatorEmail})</span>}
                </span>
                <span>
                  <strong>Visibility:</strong> {item.visibility || 'public'}
                </span>
                <span>
                  <strong>Added:</strong> {new Date(item.createdAt).toLocaleDateString()}
                </span>
              </div>

              <div className="fad-card-actions">
                {isPending && (
                  <button
                    className="fad-btn fad-btn-approve"
                    onClick={() => onModerate(type, item._id, 'APPROVED')}
                  >
                    ✓ Approve
                  </button>
                )}
                {isPending && (
                  <button
                    className="fad-btn fad-btn-reject"
                    onClick={() => setRejectTarget(item)}
                  >
                    ✕ Reject
                  </button>
                )}
                {status === 'APPROVED' && (
                  <button
                    className="fad-btn fad-btn-reject"
                    onClick={() => setRejectTarget(item)}
                  >
                    Revoke Approval
                  </button>
                )}
                <button
                  className="fad-btn fad-btn-danger fad-btn-sm"
                  onClick={() => onDelete(type, item._id, item.title)}
                >
                  Delete
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
};

// ── Main component ────────────────────────────────────────────
const FitnessAdminDashboard = ({ embedded = false }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [metrics, setMetrics] = useState(null);
  const [items, setItems] = useState({ exercises: [], workouts: [], programs: [] });
  const [loading, setLoading] = useState(false);

  // Per-tab filters
  const defaultFilters = { status: 'PENDING', creator: 'all', search: '' };
  const [filters, setFilters] = useState({
    exercises: { ...defaultFilters },
    workouts: { ...defaultFilters },
    programs: { ...defaultFilters }
  });

  const token = () => localStorage.getItem('token');

  const fetchMetrics = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/fitness/metrics`, {
        headers: { Authorization: `Bearer ${token()}` }
      });
      if (!res.ok) throw new Error('Failed to load metrics');
      setMetrics(await res.json());
    } catch (err) {
      toast.error('Failed to load fitness metrics');
    }
  }, []);

  const fetchItems = useCallback(async (type) => {
    setLoading(true);
    try {
      const f = filters[type];
      const params = new URLSearchParams();
      if (f.status !== 'all') params.set('status', f.status);
      if (f.creator !== 'all') params.set('creator', f.creator);

      const res = await fetch(`${API}/admin/fitness/${type}?${params}`, {
        headers: { Authorization: `Bearer ${token()}` }
      });
      if (!res.ok) throw new Error(`Failed to load ${type}`);
      const data = await res.json();
      setItems(prev => ({ ...prev, [type]: data }));
    } catch (err) {
      toast.error(`Failed to load ${type}`);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Fetch metrics on mount
  useEffect(() => { fetchMetrics(); }, [fetchMetrics]);

  // Fetch content when switching to a content tab or filters change
  useEffect(() => {
    if (activeTab !== 'overview') {
      fetchItems(activeTab);
    }
  }, [activeTab, fetchItems]);

  const handleModerate = async (type, id, status, rejectionReason) => {
    try {
      const res = await fetch(`${API}/admin/fitness/${type}/${id}/moderate`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status, rejectionReason })
      });
      if (!res.ok) throw new Error('Moderation failed');
      setItems(prev => ({
        ...prev,
        [type]: prev[type].map(item =>
          item._id === id ? { ...item, moderation_status: status, visibility: status === 'APPROVED' ? 'public' : item.visibility } : item
        )
      }));
      fetchMetrics();
      toast.success(`${type.slice(0, -1)} ${status.toLowerCase()} successfully`);
    } catch (err) {
      toast.error(`Failed to ${status.toLowerCase()} item`);
    }
  };

  const handleDelete = async (type, id, title) => {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    try {
      const res = await fetch(`${API}/admin/fitness/${type}/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token()}` }
      });
      if (!res.ok) throw new Error('Delete failed');
      setItems(prev => ({ ...prev, [type]: prev[type].filter(item => item._id !== id) }));
      fetchMetrics();
      toast.success(`${title} deleted`);
    } catch (err) {
      toast.error('Failed to delete item');
    }
  };

  const setFilter = (type, key, value) => {
    setFilters(prev => ({ ...prev, [type]: { ...prev[type], [key]: value } }));
  };

  // Client-side search filter
  const filteredItems = (type) => {
    const q = filters[type].search.toLowerCase();
    if (!q) return items[type];
    return items[type].filter(item =>
      item.title?.toLowerCase().includes(q) ||
      item.description?.toLowerCase().includes(q) ||
      item.creatorName?.toLowerCase().includes(q) ||
      item.affiliateBusinessName?.toLowerCase().includes(q)
    );
  };

  const TABS = [
    { key: 'overview', label: '📊 Overview' },
    { key: 'exercises', label: '🏋️ Exercises' },
    { key: 'workouts', label: '💪 Workouts' },
    { key: 'programs', label: '📋 Programs' }
  ];

  return (
    <div className={embedded ? 'fad-container fad-embedded' : 'fad-container'}>
      {!embedded && (
        <div className="fad-header">
          <h1>Fitness Admin Dashboard</h1>
          <p className="fad-subtitle">Review and approve coach-submitted fitness content</p>
        </div>
      )}

      <nav className="fad-tabs">
        {TABS.map(tab => (
          <button
            key={tab.key}
            className={`fad-tab ${activeTab === tab.key ? 'fad-tab-active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
            {tab.key !== 'overview' && metrics && metrics[tab.key]?.PENDING > 0 && (
              <span className="fad-tab-badge">{metrics[tab.key].PENDING}</span>
            )}
          </button>
        ))}
      </nav>

      <div className="fad-tab-content">
        {/* ── OVERVIEW ── */}
        {activeTab === 'overview' && (
          <div className="fad-overview">
            {!metrics ? (
              <p className="fad-loading">Loading metrics…</p>
            ) : (
              <>
                <div className="fad-metric-grid">
                  <MetricCard label="Exercises" data={metrics.exercises} />
                  <MetricCard label="Workouts" data={metrics.workouts} />
                  <MetricCard label="Programs" data={metrics.programs} />
                  <div className="fad-metric-card fad-metric-card-coaches">
                    <h3>Active Coaches</h3>
                    <span className="fad-metric-total">{metrics.activeCoaches}</span>
                    <p>Affiliates with fitness enabled</p>
                  </div>
                </div>

                {(metrics.exercises.PENDING + metrics.workouts.PENDING + metrics.programs.PENDING) > 0 && (
                  <div className="fad-alert">
                    <strong>⚠️ Pending Review:</strong>{' '}
                    {metrics.exercises.PENDING} exercise{metrics.exercises.PENDING !== 1 ? 's' : ''},{' '}
                    {metrics.workouts.PENDING} workout{metrics.workouts.PENDING !== 1 ? 's' : ''},{' '}
                    {metrics.programs.PENDING} program{metrics.programs.PENDING !== 1 ? 's' : ''} awaiting approval.
                  </div>
                )}

                <div className="fad-overview-guide">
                  <h2>How Fitness Content Visibility Works</h2>

                  <div className="fad-guide-grid">
                    <div className="fad-guide-card">
                      <h3>🌐 System / Seeded Content</h3>
                      <p>Exercises and workouts created by FreshFitLab (no <code>createdBy</code> field). These are always public and bypass moderation — they appear on <strong>/fitness</strong> for all visitors immediately.</p>
                    </div>

                    <div className="fad-guide-card">
                      <h3>👤 Regular Users (free &amp; paid)</h3>
                      <p>Regular users can <strong>save</strong> and <strong>clone</strong> system exercises/workouts. Cloned content is private (<code>visibility: private</code>) and only visible to them. They <strong>cannot</strong> publish to the public library.</p>
                      <p>Paid tiers (Standard, Premium) unlock higher limits on saved workouts, programs, and session history — but the content still stays private.</p>
                    </div>

                    <div className="fad-guide-card">
                      <h3>🏅 Coaches (Fitness Affiliates)</h3>
                      <p>Coaches create exercises/workouts/programs on the coach dashboard. New content starts as <code>DRAFT</code> (private). When the coach clicks <strong>"Publish"</strong>, it moves to <code>PENDING</code> and enters this review queue.</p>
                      <p>Once you <strong>Approve</strong> here, it becomes <code>APPROVED + public</code> and appears in the public <strong>/fitness</strong> library for all users. Coach clients see even their DRAFT content via their coach relationship.</p>
                    </div>

                    <div className="fad-guide-card">
                      <h3>📋 Programs (Coaches Only)</h3>
                      <p>Programs can be <strong>free</strong> or <strong>paid</strong>. Free programs are accessible to anyone after approval. Paid programs require a user to be connected to the coach as a client. Coaches set pricing when creating a program.</p>
                    </div>
                  </div>

                  <div className="fad-guide-flow">
                    <h3>Approval Flow</h3>
                    <div className="fad-flow-steps">
                      <div className="fad-flow-step"><span className="fad-badge fad-badge-draft">DRAFT</span><span>Coach creates content</span></div>
                      <div className="fad-flow-arrow">→</div>
                      <div className="fad-flow-step"><span className="fad-badge fad-badge-pending">PENDING</span><span>Coach publishes</span></div>
                      <div className="fad-flow-arrow">→</div>
                      <div className="fad-flow-step"><span className="fad-badge fad-badge-approved">APPROVED</span><span>Admin approves here</span></div>
                    </div>
                    <p className="fad-flow-note">Rejected content goes back to DRAFT — the coach can edit and resubmit.</p>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* ── CONTENT TABS ── */}
        {['exercises', 'workouts', 'programs'].includes(activeTab) && (
          <div className="fad-content-tab">
            <div className="fad-filter-bar">
              <input
                type="text"
                placeholder={`Search ${activeTab}…`}
                value={filters[activeTab].search}
                onChange={e => setFilter(activeTab, 'search', e.target.value)}
                className="fad-search"
              />
              <select
                value={filters[activeTab].status}
                onChange={e => setFilter(activeTab, 'status', e.target.value)}
                className="fad-select"
              >
                <option value="all">All Statuses</option>
                <option value="PENDING">Pending</option>
                <option value="APPROVED">Approved</option>
                <option value="REJECTED">Rejected</option>
                <option value="DRAFT">Draft</option>
              </select>
              <select
                value={filters[activeTab].creator}
                onChange={e => setFilter(activeTab, 'creator', e.target.value)}
                className="fad-select"
              >
                <option value="all">All Creators</option>
                <option value="coach">Coach-Created</option>
                <option value="system">System / Seeded</option>
              </select>
              <button
                className="fad-btn fad-btn-secondary"
                onClick={() => fetchItems(activeTab)}
              >
                ↻ Refresh
              </button>
            </div>

            {loading ? (
              <p className="fad-loading">Loading {activeTab}…</p>
            ) : (
              <ContentTable
                type={activeTab}
                items={filteredItems(activeTab)}
                onModerate={handleModerate}
                onDelete={handleDelete}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FitnessAdminDashboard;
